import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Plus, Search, Gift, Cake, Heart, Calendar, Pencil, Trash2,
  Mail, Clock, Send, User
} from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPES = [
  { value: 'birthday', label: 'Birthday', icon: Cake, color: 'bg-pink-100 text-pink-700' },
  { value: 'anniversary', label: 'Anniversary', icon: Heart, color: 'bg-red-100 text-red-700' },
  { value: 'membership', label: 'Membership', icon: Gift, color: 'bg-purple-100 text-purple-700' },
  { value: 'custom', label: 'Custom', icon: Calendar, color: 'bg-blue-100 text-blue-700' },
];
const REWARD_TYPES = [
  { value: 'none', label: 'No Reward' },
  { value: 'discount', label: 'Discount' },
  { value: 'points', label: 'Bonus Points' },
  { value: 'gift', label: 'Free Gift' },
  { value: 'voucher', label: 'Voucher' },
];

function timeAgo(d) { if (!d) return ''; const s = Math.floor((new Date() - new Date(d)) / 1000); if (s < 3600) return `${Math.floor(s / 60)}m ago`; if (s < 86400) return `${Math.floor(s / 3600)}h ago`; return `${Math.floor(s / 86400)}d ago`; }

function CelebrationModal({ open, onClose, onSaved, editing }) {
  const [form, setForm] = useState({ customer_name: '', customer_email: '', celebration_type: 'birthday', date: '', auto_send: true, message: '', reward_type: 'none', reward_value: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (editing) setForm({ customer_name: editing.customer_name || '', customer_email: editing.customer_email || '', celebration_type: editing.celebration_type || 'birthday', date: editing.date || '', auto_send: editing.auto_send !== false, message: editing.message || '', reward_type: editing.reward_type || 'none', reward_value: editing.reward_value || '' });
    else setForm({ customer_name: '', customer_email: '', celebration_type: 'birthday', date: '', auto_send: true, message: '', reward_type: 'none', reward_value: '' });
  }, [editing, open]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) { toast.error('Name required'); return; }
    if (!form.date) { toast.error('Date required'); return; }
    setSaving(true);
    try {
      const { data } = editing
        ? await axios.put(`${API}/api/retention/celebrations/${editing.id}`, form)
        : await axios.post(`${API}/api/retention/celebrations`, form);
      toast.success(editing ? 'Updated' : 'Celebration added');
      onSaved(data, !!editing); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle className="flex items-center gap-2"><Gift className="h-5 w-5 text-pink-500" />{editing ? 'Edit' : 'Add'} Celebration</DialogTitle><DialogDescription>Track customer special dates</DialogDescription></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <div><Label>Customer Name *</Label><Input value={form.customer_name} onChange={e => setForm(p => ({...p, customer_name: e.target.value}))} data-testid="celeb-name" /></div>
          <div><Label>Email</Label><Input value={form.customer_email} onChange={e => setForm(p => ({...p, customer_email: e.target.value}))} data-testid="celeb-email" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label><Select value={form.celebration_type} onValueChange={v => setForm(p => ({...p, celebration_type: v}))}><SelectTrigger data-testid="celeb-type"><SelectValue /></SelectTrigger><SelectContent>{TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Date *</Label><Input type="date" value={form.date} onChange={e => setForm(p => ({...p, date: e.target.value}))} data-testid="celeb-date" /></div>
          </div>
          <div><Label>Message</Label><Textarea value={form.message} onChange={e => setForm(p => ({...p, message: e.target.value}))} rows={2} placeholder="Happy Birthday! Here's a special gift..." data-testid="celeb-message" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Reward Type</Label><Select value={form.reward_type} onValueChange={v => setForm(p => ({...p, reward_type: v}))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{REWARD_TYPES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Reward Value</Label><Input value={form.reward_value} onChange={e => setForm(p => ({...p, reward_value: e.target.value}))} placeholder="20%" /></div>
          </div>
          <div className="flex items-center gap-2"><Switch checked={form.auto_send} onCheckedChange={v => setForm(p => ({...p, auto_send: v}))} /><Label className="text-sm">Auto-send greeting</Label></div>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" disabled={saving} className="bg-pink-600 hover:bg-pink-700" data-testid="save-celeb-btn">{saving ? 'Saving...' : editing ? 'Update' : 'Add Celebration'}</Button></DialogFooter>
        </form></DialogContent></Dialog>
  );
}

export default function CelebrationsPage() {
  const [celebrations, setCelebrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filterType !== 'all') params.celebration_type = filterType;
      if (search) params.search = search;
      const { data } = await axios.get(`${API}/api/retention/celebrations`, { params,  });
      setCelebrations(data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [search, filterType]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaved = (c, isUpdate) => { if (isUpdate) setCelebrations(p => p.map(x => x.id === c.id ? c : x)); else setCelebrations(p => [c, ...p]); };
  const handleDelete = async (id) => { if (!window.confirm('Delete celebration?')) return; try { await axios.delete(`${API}/api/retention/celebrations/${id}`); setCelebrations(p => p.filter(c => c.id !== id)); toast.success('Deleted'); } catch { toast.error('Failed'); } };

  const upcomingCount = celebrations.filter(c => {
    if (!c.date) return false;
    const d = new Date(c.date);
    const now = new Date();
    d.setFullYear(now.getFullYear());
    const diff = (d - now) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= 30;
  }).length;

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="celebrations-page">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1"><div className="w-10 h-10 rounded-xl bg-pink-50 flex items-center justify-center"><Gift className="h-5 w-5 text-pink-500" /></div><div><h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Birthdays & Anniversaries<PageInfoTooltip pageKey="celebrations" /></h1><p className="text-xs text-gray-400">{celebrations.length} tracked · {upcomingCount} upcoming this month</p></div></div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" /><Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="pl-9 w-36 h-9 text-sm" data-testid="celeb-search" /></div>
          <Select value={filterType} onValueChange={setFilterType}><SelectTrigger className="w-32 h-9 text-sm"><SelectValue placeholder="Type" /></SelectTrigger><SelectContent><SelectItem value="all">All Types</SelectItem>{TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select>
          <Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-pink-600 hover:bg-pink-700 h-9 gap-1.5" data-testid="new-celeb-btn"><Plus className="h-4 w-4" /> Add Celebration</Button>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-pink-500 border-t-transparent animate-spin" /></div> : celebrations.length === 0 ? (
        <Card className="border-dashed border-2 border-gray-300"><CardContent className="p-10 text-center"><Gift className="h-12 w-12 text-gray-300 mx-auto mb-3" /><h3 className="text-lg font-semibold text-gray-700 mb-1">No celebrations tracked</h3><p className="text-sm text-gray-400 mb-4">Start tracking birthdays and anniversaries</p><Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-pink-600 hover:bg-pink-700 gap-1.5"><Plus className="h-4 w-4" /> Add Celebration</Button></CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {celebrations.map(c => {
            const typeInfo = TYPES.find(t => t.value === c.celebration_type) || TYPES[0];
            const IconComp = typeInfo.icon;
            const formattedDate = c.date ? new Date(c.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
            return (
              <Card key={c.id} className="border-gray-200 hover:shadow-md transition-shadow" data-testid={`celeb-card-${c.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl ${typeInfo.color} flex items-center justify-center flex-shrink-0`}><IconComp className="h-5 w-5" /></div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-bold text-gray-900">{c.customer_name}</h3>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge className={`${typeInfo.color} text-[9px]`}>{typeInfo.label}</Badge>
                        <span className="text-[10px] text-gray-400">{formattedDate}</span>
                      </div>
                      {c.customer_email && <p className="text-[10px] text-gray-400 flex items-center gap-0.5 mt-1"><Mail className="h-2.5 w-2.5" />{c.customer_email}</p>}
                      {c.message && <p className="text-xs text-gray-500 mt-1.5 line-clamp-2">{c.message}</p>}
                      <div className="flex items-center gap-2 mt-2">
                        {c.reward_type !== 'none' && <Badge variant="outline" className="text-[9px]">{c.reward_type}: {c.reward_value}</Badge>}
                        {c.auto_send && <Badge className="bg-green-100 text-green-700 text-[9px]">Auto-send</Badge>}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1 justify-end mt-2">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditing(c); setModalOpen(true); }}><Pencil className="h-3.5 w-3.5 text-gray-400" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(c.id)}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <CelebrationModal open={modalOpen} onClose={() => { setModalOpen(false); setEditing(null); }} onSaved={handleSaved} editing={editing} />
    </div>
  );
}
